import { configureStore } from '@reduxjs/toolkit';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import authReducer from './slices/authSlice';
import profileReducer from './slices/profileSlice';
import activityReducer from './slices/activitySlice';
import chatReducer from './slices/chatSlice';
import planReducer from './slices/planSlice';
import settingsReducer from './slices/settingsSlice';
import notificationReducer from './slices/notificationSlice';
import referralReducer from './slices/referralSlice';
import discoveryReducer from './slices/discoverySlice';

import adminReducer from './slices/adminSlice';

// Load discovery state from localStorage
const loadDiscoveryState = () => {
  try {
    const serialized = localStorage.getItem('discoveryCache');
    if (!serialized) return undefined;
    const parsed = JSON.parse(serialized);
    // Check if cache is still valid (30 minutes for better UX)
    const now = Date.now();
    if (parsed.lastFetched && (now - parsed.lastFetched) < 30 * 60 * 1000) {
      // Return cached state but DON'T include filters - always start fresh
      return {
        profiles: parsed.profiles || [],
        pagination: parsed.pagination || { page: 1, limit: 20, total: 0, pages: 0 },
        lastFetched: parsed.lastFetched || 0,
        // Don't restore filters - let the component set them fresh
      };
    }
    // Clear expired cache
    localStorage.removeItem('discoveryCache');
    return undefined;
  } catch {
    return undefined;
  }
};

// Save discovery state to localStorage
const saveDiscoveryState = (state: any) => {
  try {
    const serialized = JSON.stringify({
      profiles: state.profiles,
      pagination: state.pagination,
      // Don't save filters - they should be fresh each time
      lastFetched: state.lastFetched
    });
    localStorage.setItem('discoveryCache', serialized);
  } catch {
    // Ignore errors
  }
};

export const store = configureStore({
  reducer: {
    auth: authReducer,
    profile: profileReducer,
    activity: activityReducer,
    chat: chatReducer,
    plan: planReducer,
    settings: settingsReducer,
    notification: notificationReducer,
    referral: referralReducer,
    discovery: discoveryReducer,
    admin: adminReducer,
  },
  preloadedState: {
    discovery: {
      // Start with defaults
      profiles: [],
      loading: false,
      backgroundLoading: false,
      error: null,
      pagination: { page: 1, limit: 20, total: 0, pages: 0 },
      filters: { page: 1, limit: 20 },
      lastFetched: 0,
      cacheExpiry: 30 * 60 * 1000,
      shouldFetchMore: false,
      // Then override with cached data if available
      ...loadDiscoveryState(),
    } as any
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }),
});

// Subscribe to store changes and save discovery state
store.subscribe(() => {
  const state = store.getState();
  saveDiscoveryState(state.discovery);
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

// Typed hooks
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;